import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Icon } from '@ui/Icon';
import {
  Report,
  useGetScalingSize,
  figmaPxToWidthPercent,
  REPORT_BORDER_WIDTH_PERCENT,
} from '@ui/Report';
import { useProposalsBarChartValues } from '@features/proposals/use-proposals.hooks';
import {
  barScaledCss,
  getProposalsOverviewBarFootRuleWidthCss,
  getProposalsOverviewBarGapFromVarCss,
  getProposalsOverviewBarWidthCss,
  getProposalsOverviewBarWidthThumbnailCss,
  REPORT_BAR_WIDTH_VAR,
} from '@features/proposals/proposals-overview-bar-layout.helpers';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import type { IProposal } from '@shared/models/proposals.model';
import VerticalBar from '@features/proposals/ProposalsOverview/VerticalBar';

import { ComparisonHorizontalBar } from './ComparisonHorizontalBar';
import { ProductGroupsDonutBlock } from './ProductGroupsDonutBlock';
import {
  PRODUCT_GROUPS_LABEL_VALUE,
  PRODUCT_GROUPS_TEXT_COLOR,
  REPORT_SECTION_HEADING,
  SBEF_LEGEND_TEXT_FONT_SIZE,
} from './report-product-groups.typography';
import {
  SBEF_BAR_CHART_LEFT_PERCENT,
  SBEF_BAR_CHART_WIDTH_PERCENT,
  SBEF_BAR_LIST,
  SBEF_BAR_LIST_TOP_PERCENT,
  SBEF_BAR_LIST_HEIGHT_PERCENT,
  SBEF_BAR_LIST_BAR_HEIGHT_PLOT_PERCENT,
  SBEF_BAR_LIST_ROW_GAP_PLOT_PERCENT,
  COVERAGE_DONUT_TOP_PERCENT,
} from './report-product-groups.constants';
import type {
  ProductGroupsChartData,
  SbefSegment,
} from './report-product-groups.model';
import { getReportSummaryBarDiffValue } from '../report-summary.helpers';
import { PageHeaderBlock } from './PageHeaderBlock';
import { CoverageDonut } from './CoverageDonut';
import { placeSbefHorizontalBars } from './sbef-segments.helpers';
import { OffsetBox } from './OffsetBox';
import { ProductGroupsProposalSummaryKpi } from './ProductGroupsProposalSummaryKpi';

const { Page, Text } = Report;

/** Leader-line legend sizing — scaling numbers fed through `getScalingSize`. */
const LEGEND = {
  textFontSize: SBEF_LEGEND_TEXT_FONT_SIZE,
  labelGap: figmaPxToWidthPercent(8),
  /** Horizontal space between the bar's right edge and the label column. */
  labelBarGap: figmaPxToWidthPercent(36),
} as const;

/** Bar width expression for a single-bar layout — matches {@link ReportSummary} formula. */
const SINGLE_BAR_WIDTH_CSS = getProposalsOverviewBarWidthCss(1);
const SINGLE_BAR_WIDTH_THUMBNAIL_CSS =
  getProposalsOverviewBarWidthThumbnailCss(1);
const SINGLE_BAR_FOOT_RULE_WIDTH_CSS =
  getProposalsOverviewBarFootRuleWidthCss(1);

type SectionHeadingProps = {
  children: React.ReactNode;
};

/** Shared "Elements" / "Products" section title above chart blocks. */
const SectionHeading: React.FC<SectionHeadingProps> = ({ children }) => {
  const getScalingSize = useGetScalingSize();

  return (
    <Text
      as="h3"
      fontSize={REPORT_SECTION_HEADING.fontSize}
      fontWeight={REPORT_SECTION_HEADING.fontWeight}
      sx={{
        color: PRODUCT_GROUPS_TEXT_COLOR,
        letterSpacing: getScalingSize(REPORT_SECTION_HEADING.letterSpacing),
        lineHeight: REPORT_SECTION_HEADING.lineHeight,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Text>
  );
};

type ReportProductGroupsProps = {
  /** Scenario id (version id for baseline, proposal id otherwise). */
  proposalId: IProposal['id'];
  /** Fallback page title — version or proposal name, shown as placeholder when unset. */
  defaultTitle: string;
  /** Donut chart data + proposal summary KPI. Omitted when scoped product groups have no emissions. */
  data?: ProductGroupsChartData;
  /** Baseline tender page: hide reduction KPI and diff chip on the bar. */
  isBaseline?: boolean;
  color: string;
  target: number | null | undefined;
  /** Shared CO₂e/m² max across all proposals — used as bar scale. */
  co2ePerGfaMax: number;
  /** Per-SBEF-group split of the proposal bar. Empty on the baseline page. */
  sbefSegments: SbefSegment[];
  footer?: React.ReactNode;
};

export const ReportProductGroups: React.FC<ReportProductGroupsProps> = ({
  proposalId,
  defaultTitle,
  data,
  isBaseline = false,
  color,
  target,
  co2ePerGfaMax,
  sbefSegments,
  footer,
}) => {
  const { classes } = useStyles();

  return (
    <Page padding={0} verticalAlign="stretch" hideFooter footer={footer}>
      <PageHeaderBlock
        pageId="productGroups"
        defaultTitle={defaultTitle}
        proposalId={proposalId}
        isBaseline={isBaseline}
      />

      {/* Bar chart in the freed-up left+middle area */}
      <Box
        className={classes.barChartContainer}
        sx={{
          top: `${SBEF_BAR_LIST_TOP_PERCENT}%`,
          left: `${SBEF_BAR_CHART_LEFT_PERCENT}%`,
          width: `${SBEF_BAR_CHART_WIDTH_PERCENT}%`,
          height: `${SBEF_BAR_LIST_HEIGHT_PERCENT}%`,
        }}
      >
        <BarChartSlot
          proposalId={proposalId}
          color={color}
          co2ePerGfaMax={co2ePerGfaMax}
          target={target}
          isBaseline={isBaseline}
          sbefSegments={sbefSegments}
        />
      </Box>

      {!isBaseline && data && (
        <ProductGroupsProposalSummaryKpi summary={data.proposalSummary} />
      )}

      {data && (
        <Box
          className={classes.elementsCoverage}
          sx={{
            top: `${COVERAGE_DONUT_TOP_PERCENT}%`,
            left: `${SBEF_BAR_CHART_LEFT_PERCENT}%`,
            width: `${SBEF_BAR_CHART_WIDTH_PERCENT}%`,
          }}
        >
          <CoverageDonut
            percent={data.contentCoveragePercent}
            label="Coverage ratio"
          />
        </Box>
      )}

      {data && (
        <Box
          className={classes.productGroupsCoverage}
          sx={{
            top: `${COVERAGE_DONUT_TOP_PERCENT}%`,
            left: `${figmaPxToWidthPercent(1631)}%`,
          }}
        >
          <CoverageDonut
            percent={data.epdCoveragePercent}
            label="Supplier data (EPD)"
          />
        </Box>
      )}

      {data && <ProductGroupsDonutBlock key={proposalId} data={data} />}
    </Page>
  );
};

// ---------------------------------------------------------------------------
// BarChartSlot — baseline page renders the plain total bar (mirrors
// ReportSummary). Proposal pages render the per-SBEF-group horizontal bar
// list instead (SbefElementsBarList).
// ---------------------------------------------------------------------------

type BarChartSlotProps = {
  proposalId: IProposal['id'];
  color: string;
  co2ePerGfaMax: number;
  target: number | null | undefined;
  isBaseline: boolean;
  sbefSegments: SbefSegment[];
};

const BarChartSlot: React.FC<BarChartSlotProps> = ({
  proposalId,
  color,
  co2ePerGfaMax,
  target,
  isBaseline,
  sbefSegments,
}) => {
  const { co2eChartData, co2ePerGFA } = useProposalsBarChartValues(proposalId);
  const { classes } = useBarChartSlotStyles();

  const showSegmented = sbefSegments.length > 0;
  if (showSegmented) {
    return <SbefElementsBarList segments={sbefSegments} color={color} />;
  }

  const diffValue = getReportSummaryBarDiffValue({
    isBaseline,
    target,
    co2ePerGfa: co2ePerGFA,
  });

  return (
    <Box className={classes.root}>
      <Box className={classes.barPlot}>
        <Box className={classes.barFootRule} />

        <Box className={classes.barsRow}>
          <Box className={classes.barColumn}>
            <Box className={classes.barSlot}>
              <VerticalBar
                fluid
                color={color}
                co2eChartData={co2eChartData}
                co2ePerGFA={co2ePerGFA}
                co2ePerGfaMax={co2ePerGfaMax}
                diffValue={diffValue}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// SbefElementsBarList — one horizontal bar per SBEF group: bar length ∝
// baseline CO₂e, colored fill ∝ proposal CO₂e on the same scale, label
// directly following the bar (no leader line).
// ---------------------------------------------------------------------------

type SbefElementsBarListProps = {
  segments: SbefSegment[];
  color: string;
};

const SbefElementsBarList: React.FC<SbefElementsBarListProps> = ({
  segments,
  color,
}) => {
  const getScalingSize = useGetScalingSize();
  const { classes } = useElementsBarListStyles({ getScalingSize });

  const placed = placeSbefHorizontalBars(segments);
  const halfBarHeightPct = SBEF_BAR_LIST_BAR_HEIGHT_PLOT_PERCENT / 2;
  const firstRowTopPct = placed[0]?.rowTopPct ?? 50;
  const lastRowTopPct = placed.at(-1)?.rowTopPct ?? 50;
  const axisTopPct =
    firstRowTopPct - halfBarHeightPct - SBEF_BAR_LIST_ROW_GAP_PLOT_PERCENT;
  const axisBottomPct =
    100 -
    (lastRowTopPct + halfBarHeightPct + SBEF_BAR_LIST_ROW_GAP_PLOT_PERCENT);

  return (
    <Box className={classes.root}>
      <Box className={classes.plot}>
        <Box className={classes.heading} sx={{ top: `${axisTopPct}%` }}>
          <SectionHeading>Elements</SectionHeading>
        </Box>
        <Box
          className={classes.axisLine}
          sx={{ top: `${axisTopPct}%`, bottom: `${axisBottomPct}%` }}
        />
        {placed.map((segment) => {
          const offsetValue = segment.proposalCo2e - segment.baselineCo2e;
          const trackScalePct = Math.max(
            segment.totalWidthPct,
            segment.fillWidthPct,
            1,
          );
          const baselineOfTrackPct =
            (segment.totalWidthPct / trackScalePct) * 100;
          const fillOfTrackPct = (segment.fillWidthPct / trackScalePct) * 100;

          return (
            <Box
              key={segment.groupKey}
              className={classes.row}
              sx={{ top: `${segment.rowTopPct}%` }}
            >
              <Box
                className={classes.barTrack}
                sx={{
                  width: `${(trackScalePct / 100) * SBEF_BAR_LIST.maxTrackWidthPercent}%`,
                }}
              >
                <ComparisonHorizontalBar
                  baselinePct={baselineOfTrackPct}
                  proposalPct={fillOfTrackPct}
                  color={color}
                />
              </Box>

              <Box className={classes.label}>
                <Text
                  as="span"
                  fontSize={LEGEND.textFontSize}
                  fontWeight={700}
                  sx={{
                    color: PRODUCT_GROUPS_TEXT_COLOR,
                    lineHeight: 1.2,
                    opacity: 0.8,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}
                >
                  {segment.label}
                </Text>
                <Box className={classes.valuesRow}>
                  <Box className={classes.cloudIcon}>
                    <Icon
                      category="costEmission"
                      icon="kgCO2e"
                      size={getScalingSize(PRODUCT_GROUPS_LABEL_VALUE.iconSize)}
                    />
                  </Box>
                  <Text
                    as="span"
                    fontSize={LEGEND.textFontSize}
                    fontWeight={500}
                    sx={{
                      color: PRODUCT_GROUPS_TEXT_COLOR,
                      fontVariantNumeric: 'tabular-nums',
                      lineHeight: PRODUCT_GROUPS_LABEL_VALUE.lineHeight,
                      opacity: PRODUCT_GROUPS_LABEL_VALUE.opacity,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {getDisplayNumber(segment.proposalCo2e, {
                      decimals: segment.proposalCo2e >= 10 ? 0 : 1,
                    })}
                  </Text>
                  {offsetValue !== 0 && (
                    <OffsetBox
                      value={offsetValue}
                      label={segment.label}
                      fontSize={LEGEND.textFontSize}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles()(() => ({
  barChartContainer: {
    position: 'absolute',
    zIndex: 1,
  },
  elementsCoverage: {
    display: 'flex',
    justifyContent: 'flex-end',
    position: 'absolute',
    zIndex: 1,
  },
  productGroupsCoverage: {
    display: 'flex',
    position: 'absolute',
    zIndex: 1,
  },
}));

const useBarChartSlotStyles = makeStyles()(() => ({
  root: {
    position: 'relative',
    width: '100%',
    height: '100%',
    [REPORT_BAR_WIDTH_VAR as string]: SINGLE_BAR_WIDTH_CSS,
    '[data-thumbnail] &': {
      [REPORT_BAR_WIDTH_VAR as string]: SINGLE_BAR_WIDTH_THUMBNAIL_CSS,
    },
  },
  barPlot: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '82%',
    overflow: 'visible',
    zIndex: 1,
  },
  barFootRule: {
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: SINGLE_BAR_FOOT_RULE_WIDTH_CSS,
    borderTop: `${barScaledCss(1)} solid rgba(0,0,0,0.1)`,
    pointerEvents: 'none',
    zIndex: 3,
  },
  barsRow: {
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: getProposalsOverviewBarGapFromVarCss(),
    height: '100%',
    minHeight: 0,
    overflow: 'visible',
    zIndex: 2,
  },
  barColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: `var(${REPORT_BAR_WIDTH_VAR})`,
    flexShrink: 0,
    height: '100%',
    position: 'relative',
  },
  barSlot: {
    position: 'relative',
    flex: 1,
    width: '100%',
    minHeight: 0,
  },
}));

const useElementsBarListStyles = makeStyles<{
  getScalingSize: ReturnType<typeof useGetScalingSize>;
}>()((_theme, { getScalingSize }) => ({
  root: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  plot: {
    position: 'absolute',
    inset: 0,
  },
  heading: {
    position: 'absolute',
    left: 0,
    transform: 'translateY(-100%)',
    paddingBottom: getScalingSize(figmaPxToWidthPercent(20)),
    pointerEvents: 'none',
  },
  axisLine: {
    position: 'absolute',
    left: 0,
    borderLeft: `${getScalingSize(REPORT_BORDER_WIDTH_PERCENT)} solid rgba(0,0,0,0.15)`,
    pointerEvents: 'none',
  },
  row: {
    position: 'absolute',
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    transform: 'translateY(-50%)',
  },
  barTrack: {
    position: 'relative',
    flexShrink: 0,
    height: getScalingSize(SBEF_BAR_LIST.barHeight),
  },
  label: {
    minWidth: 0,
    flex: 1,
    overflow: 'hidden',
    marginLeft: getScalingSize(SBEF_BAR_LIST.labelGap),
  },
  valuesRow: {
    display: 'flex',
    alignItems: 'center',
    gap: getScalingSize(PRODUCT_GROUPS_LABEL_VALUE.groupGap),
    marginTop: getScalingSize(figmaPxToWidthPercent(2)),
  },
  cloudIcon: {
    display: 'flex',
    opacity: PRODUCT_GROUPS_LABEL_VALUE.iconOpacity,
  },
}));
